import * as THREE from 'three';
import { LIDAR, EARTH_POSITION } from './constants';

/**
 * Creates and returns a LiDAR system with scanning equipment
 */
const createLidar = (planeBody) => {
  // Create LiDAR scanner (mounted on the bottom of the plane)
  const lidarGroup = new THREE.Group();
  
  // LiDAR base
  const baseGeometry = new THREE.CylinderGeometry(
    LIDAR.BASE.RADIUS, 
    LIDAR.BASE.RADIUS, 
    LIDAR.BASE.HEIGHT, 
    16
  );
  const baseMaterial = new THREE.MeshPhongMaterial({ color: 0x222222 });
  const base = new THREE.Mesh(baseGeometry, baseMaterial);
  lidarGroup.add(base);
  
  // LiDAR cone (rotatable scanner) - pointed downward
  const coneGeometry = new THREE.ConeGeometry(
    LIDAR.CONE.RADIUS, 
    LIDAR.CONE.HEIGHT, 
    16
  );
  const coneMaterial = new THREE.MeshPhongMaterial({ 
    color: 0x00ff00, 
    transparent: true, 
    opacity: 0.6,
    emissive: 0x00ff00,
    emissiveIntensity: 0.5
  });
  const cone = new THREE.Mesh(coneGeometry, coneMaterial);
  cone.position.y = LIDAR.CONE.POSITION_Y; // Position cone on top of base
  // Rotate cone to point downward
  cone.rotation.x = Math.PI;
  lidarGroup.add(cone);
  
  // Add LiDAR to bottom center of the airplane
  // Position slightly below the plane body
  lidarGroup.position.set(0, -0.2, 0);
  planeBody.add(lidarGroup);
  
  // Create laser beams
  const laserGroup = new THREE.Group();
  
  // Brighter, more visible laser material
  const laserMaterial = new THREE.LineBasicMaterial({ 
    color: 0x00ff00, 
    transparent: true, 
    opacity: 0.9,
    linewidth: 2 // Note: Three.js linewidth is limited by WebGL
  });
  
  // Create scanner rotation tracker
  const scanAngle = { value: 0 };
  
  return {
    lidarGroup,
    cone,
    laserGroup,
    laserMaterial,
    scanAngle,
    geometries: {
      baseGeometry,
      coneGeometry
    },
    materials: {
      baseMaterial,
      coneMaterial
    }
  };
};

/**
 * Updates the laser beams from the LiDAR system
 * Creates a circular scanning pattern on Earth centered on the line from LiDAR to Earth center
 */
const updateLasers = (airplaneGroup, lidar) => {
  const { cone, laserGroup, laserMaterial, scanAngle, lidarGroup } = lidar;
  
  // Clear previous lasers
  while(laserGroup.children.length > 0) {
    const laser = laserGroup.children[0];
    laser.geometry.dispose();
    laserGroup.remove(laser);
  }
  
  // Get world position of LiDAR cone tip
  const lidarWorldPosition = new THREE.Vector3();
  cone.getWorldPosition(lidarWorldPosition);
  
  // Earth parameters - get the actual center of the Earth
  // Earth is positioned at EARTH_POSITION, not at origin
  const earthCenter = new THREE.Vector3(
    EARTH_POSITION.X,
    EARTH_POSITION.Y,
    EARTH_POSITION.Z
  );
  const earthRadius = LIDAR.EARTH_INTERSECTION_RADIUS;
  
  // Update scanning angle (controls rotation of the circular pattern)
  scanAngle.value += LIDAR.SCANNING.ANGLE_INCREMENT;
  
  // Calculate direction from LiDAR to Earth center (this is the central axis of our cone)
  // This ensures the LiDAR always points toward Earth center regardless of airplane position
  const directionToEarthCenter = new THREE.Vector3()
    .subVectors(earthCenter, lidarWorldPosition)
    .normalize();
    
  // Create circular pattern with laser beams
  const numBeams = LIDAR.SCANNING.NUM_BEAMS;
  const coneAngle = LIDAR.SCANNING.CONE_ANGLE;
  
  // Update the LiDAR cone's orientation to point toward Earth center
  // This helps visualize the direction of scanning
  if (lidarGroup) {
    // Create a look-at rotation that makes the LiDAR cone point toward Earth center
    const coneWorldPosition = new THREE.Vector3();
    cone.getWorldPosition(coneWorldPosition);
    
    // Get LiDAR's parent local matrix
    const parentMatrixWorld = new THREE.Matrix4();
    // Use the planeBody that's passed in the parameters
    parentMatrixWorld.copy(airplaneGroup.matrixWorld);
    const parentMatrixWorldInv = new THREE.Matrix4().copy(parentMatrixWorld).invert();
    
    // Direction in world space
    const worldDirection = new THREE.Vector3().subVectors(earthCenter, coneWorldPosition).normalize();
    
    // Convert to direction in parent's local space
    const localDirection = worldDirection.clone().transformDirection(parentMatrixWorldInv);
    
    // Set the lidarGroup's rotation to aim toward the Earth
    // We maintain the existing rotation around the axis that points to Earth
    lidarGroup.lookAt(localDirection.add(lidarGroup.position));
  }
  
  for(let i = 0; i < numBeams; i++) {
    // Calculate angle for this beam in the circular pattern (evenly distributed around 360 degrees)
    const beamAngle = (i / numBeams) * Math.PI * 2 + scanAngle.value;
    
    // Create a basis for our scanning cone that is always oriented toward the Earth center
    // Find an arbitrary vector perpendicular to our direction vector
    const referenceVector = new THREE.Vector3(1, 0, 0);
    if (Math.abs(directionToEarthCenter.dot(referenceVector)) > 0.9) {
      // If our axis is close to the reference, choose a different reference
      referenceVector.set(0, 1, 0);
    }
    
    // Create orthogonal basis for the scanning plane
    const basisX = new THREE.Vector3()
      .crossVectors(directionToEarthCenter, referenceVector)
      .normalize();
    const basisZ = new THREE.Vector3()
      .crossVectors(directionToEarthCenter, basisX)
      .normalize();
    
    // Start with direction toward Earth center
    const direction = new THREE.Vector3().copy(directionToEarthCenter);
    
    // Add circular deviation based on cone angle
    // This creates a cone of directions around the central axis
    direction.addScaledVector(basisX, Math.cos(beamAngle) * Math.sin(coneAngle));
    direction.addScaledVector(basisZ, Math.sin(beamAngle) * Math.sin(coneAngle));
    direction.normalize();
    
    // Ray-sphere intersection to find where the laser hits Earth
    // Using analytic solution for ray-sphere intersection for a sphere centered at earthCenter
    
    // Create vectors relative to the Earth center for the calculations
    const lidarRelativeToEarth = new THREE.Vector3().subVectors(lidarWorldPosition, earthCenter);
    
    // Ray-sphere intersection formula where sphere is at earthCenter
    const a = direction.dot(direction);
    const b = 2 * direction.dot(lidarRelativeToEarth);
    const c = lidarRelativeToEarth.dot(lidarRelativeToEarth) - earthRadius * earthRadius;
    
    const discriminant = b * b - 4 * a * c;
    
    // If ray intersects sphere
    if (discriminant >= 0) {
      // Calculate closest intersection (smaller value of t)
      const t = (-b - Math.sqrt(discriminant)) / (2 * a);
      
      // Only process positive t values (intersections in front of the LiDAR)
      if (t > 0) {
        // Calculate intersection point
        const intersectionPoint = new THREE.Vector3()
          .copy(lidarWorldPosition)
          .addScaledVector(direction, t);
        
        // Create the laser beam
        const laserGeometry = new THREE.BufferGeometry().setFromPoints([
          lidarWorldPosition,
          intersectionPoint
        ]);
        
        const laser = new THREE.Line(laserGeometry, laserMaterial);
        laserGroup.add(laser);
      }
    }
  }
};

/**
 * Cleanup function for LiDAR objects
 */
const cleanupLidar = (scene, lidar) => {
  const { lidarGroup, laserGroup, laserMaterial, geometries, materials } = lidar;
  
  // Clean up lasers
  while(laserGroup.children.length > 0) {
    const laser = laserGroup.children[0];
    laser.geometry.dispose();
    laserGroup.remove(laser);
  }
  scene.remove(laserGroup);
  laserMaterial.dispose();
  
  // Dispose of all geometries
  Object.values(geometries).forEach(geometry => {
    geometry.dispose();
  });
  
  // Dispose of all materials
  Object.values(materials).forEach(material => {
    material.dispose();
  });
};

export { createLidar, updateLasers, cleanupLidar };